import { exceptionMessageHandler } from '$utils'
import {
  InvoiceDateProperty,
  ISupplierStatement,
} from '@loadedhub/loaded-stock-api-sdk'
import { Dayjs } from 'dayjs'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { IStockReceived } from '$models'
import { stockReceivedService, supplierStatementService } from '$services'

interface ReportsState {
  supplierStatements: ISupplierStatement[]
  stockReceivedItems: IStockReceived[]
}

export interface ReportsSlice extends ReportsState {
  resetReportsSlice: () => void

  /**
   * Get supplier statements
   * @param from - start date
   * @param to - end date
   */
  getSupplierStatements(
    from: Dayjs,
    to: Dayjs,
    supplierId?: string,
    includeDeleted?: boolean
  ): Promise<ISupplierStatement[]>

  getStockReceivedItems(
    periodStart: Dayjs,
    periodEnd: Dayjs,
    supplierId?: string,
    includeAdjustingInvoices?: boolean
  ): Promise<IStockReceived[]>
}

const initialState: ReportsState = {
  supplierStatements: [],
  stockReceivedItems: [],
}

const createReportsSlice: StateCreator<
  ReportsSlice & SharedSlice,
  [],
  [],
  ReportsSlice
> = (set) => ({
  ...initialState,

  resetReportsSlice: () => set(initialState),

  getSupplierStatements: async (
    from,
    to,
    supplierId,
    includeDeleted: boolean = false
  ): Promise<ISupplierStatement[]> => {
    try {
      const supplierStatements: ISupplierStatement[] =
        await supplierStatementService.getSupplierStatements(
          from,
          to,
          supplierId,
          includeDeleted
        )

      set(() => ({
        supplierStatements: supplierStatements,
      }))
      return supplierStatements
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve supplier statements')
      return []
    }
  },

  getStockReceivedItems: async (
    periodStart: Dayjs,
    periodEnd: Dayjs,
    supplierId?: string,
    includeAdjustingInvoices: boolean = false
  ): Promise<IStockReceived[]> => {
    try {
      const stockReceivedItems: IStockReceived[] =
        await stockReceivedService.getStockReceivedItems(
          periodStart,
          periodEnd,
          InvoiceDateProperty.Invoiced,
          supplierId,
          includeAdjustingInvoices
        )

      set(() => ({
        stockReceivedItems: stockReceivedItems,
      }))
      return stockReceivedItems
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve stock received items')
      return []
    }
  },
})

export default createReportsSlice
